import { Alert, Box, Checkbox, Chip, Divider, FormControlLabel, Skeleton, Stack, Typography } from '@mui/material';
import { FunctionComponent, ReactNode, useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryRuleService from '../../../shared/services/library-rule/library-rule.service';
import {
  ILibraryPushClientResult,
  ILibraryPushResult,
  ILibraryRulePushStatus,
} from '../../../shared/services/library-rule/library-rule.model';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding';

const STATUS_LABEL: Record<ILibraryRulePushStatus, string> = {
  'not-pushed': 'Not pushed',
  'up-to-date': 'Up to date',
  'out-of-date': 'Out of date',
  'edited-by-client': 'Edited by client',
};

// Rules name the conflicting rule, analytic reports name the conflicting report. One
// accessor so the copy below does not have to know which resource it is rendering.
const conflictName = (row: { conflictingName?: string | null, conflictingRuleName?: string | null }) =>
  row.conflictingName ?? row.conflictingRuleName ?? null;

const STATUS_COLOUR: Record<ILibraryRulePushStatus, 'default' | 'success' | 'warning' | 'info'> = {
  'not-pushed': 'default',
  'up-to-date': 'success',
  'out-of-date': 'warning',
  'edited-by-client': 'info',
};

// What this dialog actually READS from a push target -- deliberately not "a rule target plus
// extras". Every Library resource satisfies it structurally: rules name the conflicting rule,
// reports name the conflicting report and add a resolved period, and the dialog never touches
// either resource's own link id.
export interface ILibraryPushTarget {
  id: string,
  name: string,
  status: ILibraryRulePushStatus,
  wouldConflict: boolean,
  conflictReason: string | null,
  needsOverwriteToPush: boolean,
  conflictingName?: string | null,
  conflictingRuleName?: string | null,
  reportPeriod?: { id: string | null, name: string | null, reason: string },
}

// Likewise the result: only the three fields rendered below, so a resource carrying its own
// id key (libraryRuleId vs libraryAnalyticReportId) still fits.
export interface ILibraryPushOutcome {
  message: string,
  summary: ILibraryPushResult['summary'],
  results: ILibraryPushClientResult[],
}

// The two calls this dialog makes.
export interface ILibraryPushApi {
  getPushTargets: (id: string) => Promise<{ clients: ILibraryPushTarget[] }>,
  push: (id: string, clientOrganisationIds: string[], overwriteClientEditIds: string[]) => Promise<ILibraryPushOutcome>,
}

// Both steps live in one component so the caller only wires up a single open flag -- a
// separate results dialog would mean a second piece of open-state plumbing for what the user
// experiences as one action.
export const PartnerLibraryPushDialog: FunctionComponent<{
  open: boolean,
  libraryRuleId: string,
  libraryRuleName: string,
  closeHandler: () => void,
  // Called after a push that changed something, so the list can refresh its counts.
  onPushed: () => void,
  // 'rule' | 'analytic report' | (later) 'survey'.
  resourceLabel?: string,
  // What identifies the resource to a human, used in the collision copy: a rule collides on
  // its matching value, a report on its name.
  identityNoun?: string,
  // Defaults to the rules service so existing callers are unchanged.
  api?: ILibraryPushApi,
  // Optional extra line under each client row. The reports page uses it to say which report
  // period that client would end up on, which is what makes a period fallback visible BEFORE
  // the push rather than only in the result.
  renderClientDetail?: (client: ILibraryPushTarget) => ReactNode,
}> = ({
  open, libraryRuleId, libraryRuleName, closeHandler, onPushed,
  resourceLabel = 'rule', identityNoun = 'value', api, renderClientDetail,
}) => {
  const libraryRuleService = new LibraryRuleService()
  const pushApi: ILibraryPushApi = api ?? {
    getPushTargets: (id) => libraryRuleService.getPushTargets(id),
    push: (id, ids, overwriteIds) => libraryRuleService.push(id, ids, overwriteIds),
  }
  const userNotificationsContext = useContext(UserNotificationsContext)

  const [targets, setTargets] = useState<ILibraryPushTarget[]>()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  // Clients the partner has explicitly agreed to overwrite despite their local edits.
  const [overwriteIds, setOverwriteIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [result, setResult] = useState<ILibraryPushOutcome>()

  useEffect(() => {
    pushApi.getPushTargets(libraryRuleId)
      .then(response => {
        setTargets(response.clients)
        // Pre-select everything that would actually change: a client whose copy is already
        // current, or that would be skipped anyway, is left unticked. Client-edited copies
        // are pre-selected but NOT pre-ticked for overwrite -- replacing someone's own change
        // has to be a deliberate act, never a default.
        setSelectedIds(response.clients
          .filter(client => !client.wouldConflict && client.status !== 'up-to-date')
          .map(client => client.id))
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
  }, [libraryRuleId])

  const toggleClient = (clientId: string) => {
    setSelectedIds(current => current.includes(clientId)
      ? current.filter(id => id !== clientId)
      : [...current, clientId])
  }

  // Overwriting a client's own edit is a separate, deliberate decision from choosing to push
  // to them, so it is its own tick rather than being implied by selection.
  const toggleOverwrite = (clientId: string) => {
    setOverwriteIds(current => {
      const next = current.includes(clientId)
        ? current.filter(id => id !== clientId)
        : [...current, clientId]
      // Agreeing to overwrite only means anything if the client is actually being pushed to.
      if (!current.includes(clientId)) {
        setSelectedIds(selected => selected.includes(clientId) ? selected : [...selected, clientId])
      }
      return next
    })
  }

  const allSelected = Boolean(targets && targets.length > 0 && selectedIds.length === targets.length)

  const toggleAll = () => {
    if (!targets) return
    setSelectedIds(allSelected ? [] : targets.map(client => client.id))
  }

  const submitHandler = () => {
    // Second step: the submit button just closes and reports upward.
    if (result) {
      closeHandler()
      return
    }
    if (isSubmitting || selectedIds.length === 0) return
    setIsSubmitting(true)
    pushApi.push(libraryRuleId, selectedIds, overwriteIds)
      .then(response => {
        setResult(response)
        setIsSubmitting(false)
        if (response.summary.created + response.summary.updated > 0) onPushed()
      })
      .catch(err => {
        console.log(err)
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
  }

  const conflictCount = targets?.filter(client => client.wouldConflict).length ?? 0

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={result ? 'Push complete' : `Push "${libraryRuleName}" to clients`}
      maxWidth='sm'
      cancelHandler={closeHandler}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      hideCancelButton={Boolean(result)}
      disableSaveButton={!result && selectedIds.length === 0}
      submitButtonText={result ? 'Done' : `Push to ${selectedIds.length} client${selectedIds.length === 1 ? '' : 's'}`}
      submitButtonLoadingText='Pushing'
    >
      {isLoading && <Stack spacing={1}>
        {[0, 1, 2].map(row => <Skeleton key={row} variant='rectangular' height={32} />)}
      </Stack>}

      {!isLoading && !result && <>
        {targets && targets.length === 0 &&
          <Typography variant='body2'>You do not have any clients to push to yet.</Typography>}

        {targets && targets.length > 0 && <>
          <Typography variant='body2' sx={{ mb: 1 }}>
            The clients you choose will receive this {resourceLabel}. They will be able to see it but not
            change it, and pushing again later will update their copy.
          </Typography>

          {conflictCount > 0 && <Alert severity='info' sx={{ mb: 2 }}>
            {conflictCount} client{conflictCount === 1 ? ' has' : 's have'} their own {resourceLabel} using
            this value. {conflictCount === 1 ? 'It' : 'They'} will be skipped so their existing set-up is
            left alone.
          </Alert>}

          <FormControlLabel
            control={<Checkbox checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected} onChange={toggleAll} />}
            label={<Typography variant='body2' sx={{ fontFamily: BRAND_DISPLAY_FONT }}>Select all</Typography>}
          />
          <Divider sx={{ mb: 1 }} />

          <Stack spacing={0.5}>
            {targets.map(client => (
              <Box key={client.id}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Checkbox
                    size='small'
                    checked={selectedIds.includes(client.id)}
                    onChange={() => toggleClient(client.id)}
                  />
                  <Typography variant='body2' sx={{ flex: 1 }}>{client.name}</Typography>
                  <Chip size='small' variant='outlined' color={STATUS_COLOUR[client.status]} label={STATUS_LABEL[client.status]} />
                </Stack>
                {client.wouldConflict && <Typography variant='caption' sx={{ display: 'block', pl: 5, color: 'text.secondary' }}>
                  {client.conflictReason === 'no-report-period'
                    ? 'Has no report periods yet, so will be skipped'
                    : `Already has its own ${resourceLabel}${conflictName(client) ? ` "${conflictName(client)}"` : ''} with this ${identityNoun}, so will be skipped`}
                </Typography>}
                {renderClientDetail && <Box sx={{ pl: 5 }}>{renderClientDetail(client)}</Box>}
                {client.needsOverwriteToPush && <Box sx={{ pl: 5 }}>
                  <FormControlLabel
                    control={<Checkbox
                      size='small'
                      checked={overwriteIds.includes(client.id)}
                      onChange={() => toggleOverwrite(client.id)}
                    />}
                    label={<Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      {overwriteIds.includes(client.id)
                        ? 'Their changes will be replaced with your version'
                        : 'They have changed this, so it is skipped unless you tick to overwrite'}
                    </Typography>}
                  />
                </Box>}
              </Box>
            ))}
          </Stack>
        </>}
      </>}

      {result && <>
        <Typography variant='body2' sx={{ mb: 2 }}>{result.message}.</Typography>
        <Stack spacing={0.5}>
          {result.results.map(clientResult => (
            <Box key={clientResult.clientOrganisationId}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography variant='body2' sx={{ flex: 1 }}>{clientResult.clientName ?? clientResult.clientOrganisationId}</Typography>
                <Chip
                  size='small'
                  variant='outlined'
                  color={clientResult.status === 'failed' ? 'error' : clientResult.status === 'skipped' ? 'warning' : 'success'}
                  label={clientResult.status === 'created' ? 'Added' : clientResult.status === 'updated' ? 'Updated' : clientResult.status === 'skipped' ? 'Skipped' : 'Failed'}
                />
              </Stack>
              {clientResult.status === 'updated' && clientResult.overwroteClientEdit &&
                <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                  Their own changes were replaced with your version
                </Typography>}
              {clientResult.status === 'skipped' && <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                {clientResult.reason === 'client-edited'
                  ? `They have changed their copy, so it was left as-is. Push again and tick to overwrite if you want to replace it`
                  : clientResult.reason === 'no-report-period'
                    ? `They have no report periods, so there is nothing for this ${resourceLabel} to cover`
                    : clientResult.reason === 'value-conflict-library'
                      ? `Another library ${resourceLabel}${conflictName(clientResult) ? ` ("${conflictName(clientResult)}")` : ''} already uses this ${identityNoun} here`
                      : `They have their own ${resourceLabel}${conflictName(clientResult) ? ` ("${conflictName(clientResult)}")` : ''} with this ${identityNoun}, so it was left untouched`}
              </Typography>}
              {clientResult.status === 'failed' && <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                Something went wrong for this client. Try pushing to them again
              </Typography>}
            </Box>
          ))}
        </Stack>
      </>}
    </DialogWrapper>
  )
}
